import React, { useState, useRef, useEffect } from 'react';

/**
 * Simple draggable and resizable field component
 * Implements basic drag and drop functionality without external dependencies
 */
const DraggableField = ({
  field,
  paperSize,
  canvasDimensions,
  isSelected,
  onSelect,
  onUpdate,
  onRemove,
  children
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const fieldRef = useRef(null);

  // Convert inches to canvas pixels
  const inchesToCanvasPixels = (inches, canvasDimension, paperDimension) => {
    return (inches / paperDimension) * canvasDimension;
  };

  // Convert canvas pixels to inches
  const canvasPixelsToInches = (pixels, canvasDimension, paperDimension) => {
    return (pixels / canvasDimension) * paperDimension;
  };

  // Calculate field position and size in pixels
  const x = inchesToCanvasPixels(field.x, canvasDimensions.width, paperSize.width);
  const y = inchesToCanvasPixels(field.y, canvasDimensions.height, paperSize.height);
  const width = inchesToCanvasPixels(field.width, canvasDimensions.width, paperSize.width);
  const height = inchesToCanvasPixels(field.height, canvasDimensions.height, paperSize.height);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (e.target.classList.contains('resize-handle')) {
      return; // Let resize handle take precedence
    }

    e.preventDefault();
    e.stopPropagation();
    onSelect(field);
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
    setInitialPosition({ x: field.x, y: field.y });
  };

  // Handle mouse down for resizing
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect(field);
    
    setIsResizing(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
    setInitialPosition({ 
      width: field.width, 
      height: field.height 
    });
  };

  // Handle mouse move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        const deltaXInches = canvasPixelsToInches(deltaX, canvasDimensions.width, paperSize.width);
        const deltaYInches = canvasPixelsToInches(deltaY, canvasDimensions.height, paperSize.height);
        
        // Allow free movement - only prevent going too far negative
        const newX = Math.max(-field.width, initialPosition.x + deltaXInches);
        const newY = Math.max(-field.height, initialPosition.y + deltaYInches);
        
        onUpdate(field.id, { x: newX, y: newY });
      } else if (isResizing) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        
        const deltaWidthInches = canvasPixelsToInches(deltaX, canvasDimensions.width, paperSize.width);
        const deltaHeightInches = canvasPixelsToInches(deltaY, canvasDimensions.height, paperSize.height);
        
        // Allow free resizing with reasonable minimums, no maximum constraints
        const newWidth = Math.max(0.1, initialPosition.width + deltaWidthInches);
        const newHeight = Math.max(0.1, initialPosition.height + deltaHeightInches);
        
        onUpdate(field.id, { width: newWidth, height: newHeight });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, initialPosition, field, paperSize, canvasDimensions, onUpdate]);

  // Handle double click to select/edit
  const handleDoubleClick = () => {
    onSelect(field);
  };

  // Get rotation value from field style
  const rotation = field.style?.rotation || 0;

  return (
    <div
      ref={fieldRef}
      className={`absolute cursor-move border-2 transition-all select-none ${
        isSelected 
          ? 'border-blue-500 bg-blue-50 shadow-lg' 
          : 'border-transparent hover:border-gray-400 hover:shadow-md'
      } ${isDragging ? 'z-50' : 'z-10'}`}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        minWidth: '20px',
        minHeight: '20px',
        transform: `rotate(${rotation}deg)`,
        transformOrigin: 'center center'
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {/* Field content */}
      <div className="w-full h-full overflow-hidden" style={{ transform: 'rotate(0deg)' }}>
        {children}
      </div>

      {/* Selection indicators and controls */}
      {isSelected && (
        <>
          {/* Field label */}
          <div className="absolute -top-6 left-0 bg-blue-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
            {field.label}
          </div>

          {/* Resize handle */}
          <div
            className="resize-handle absolute bottom-0 right-0 w-3 h-3 bg-blue-600 cursor-se-resize border border-white"
            onMouseDown={handleResizeMouseDown}
          />

          {/* Remove button */}
          <button
            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600 flex items-center justify-center"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(field.id);
            }}
            title="Remove field"
          >
            ×
          </button>
        </>
      )}

      {/* Dragging overlay */}
      {isDragging && (
        <div className="absolute inset-0 bg-blue-200 bg-opacity-50 pointer-events-none" />
      )}
    </div>
  );
};

export default DraggableField;
